import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { ACTION_DISPLAY_TYPES, CARD_DIRECTIVE_TYPES } from "../../../constants/enums";
import useErrorModal from "../../../hooks/useErrorModal";
import { GetApiData } from "../../../api/general";
import { ROUTES } from "../../../constants/api";
import { UpdateFeatureFlags } from "../../../actions/user";
import { GetFeatureFlagsResponse } from "../../../types/api/user";
import {
  APIButtonType,
  APIModalType,
  GetMyCardResponse,
} from "../../../types/api/card";
import { useMyCardActions } from "../../../hooks/useMyCardActions";
import { useMyCardModals } from "../../../hooks/useMyCardModals";

import Action from "../../Common/Action";
import Alerts from "../../Dashboard/Alerts";
import PINCheckModal from "../PINCheckModal/PINCheckModal";
import CardDetailsModal from "../CardDetailsModal/CardDetailsModal";
import MyCardDirectiveModal from "../MyCardDirectiveModal";
import SetupFaceCheckModal from "../SetupFaceCheckModal/SetupFaceCheckModal";
import i18n from "../../../i18n/config";

interface CardSectionProps {
  data: GetMyCardResponse;
  getMyCardData: () => Promise<void> | void;
}

const CardSection = ({ data, getMyCardData }: CardSectionProps) => {
  const dispatch = useDispatch();
  const openErrorModal = useErrorModal();

  const [directiveModalData, setDirectiveModalData] = useState<APIModalType | null>(null);

  const getFeatureFlags = async () => {
    try {
      const response = await GetApiData<GetFeatureFlagsResponse>(ROUTES.USER.FEATURE_FLAGS);
      dispatch(UpdateFeatureFlags(response.featureFlags));
    } catch (err) {
      openErrorModal(err);
    }
  };

  const { loadingButtonId, lockUnlock } = useMyCardActions(
    data,
    getMyCardData,
    getFeatureFlags,
    openErrorModal
  );
  const { activeModal, setActiveModal, setModalOpen, cardDetails, submitting, submitPin } =
    useMyCardModals(data, openErrorModal);

  const handleClick = (button: APIButtonType) => {
    switch (button.directiveType) {
      case CARD_DIRECTIVE_TYPES.MYCARD_LOCK_CARD:
      case CARD_DIRECTIVE_TYPES.MYCARD_UNLOCK_CARD:
        lockUnlock(button);
        break;
      case CARD_DIRECTIVE_TYPES.MYCARD_SHOW_DETAILS:
        setActiveModal("pin");
        break;
      case CARD_DIRECTIVE_TYPES.MODAL:
        setDirectiveModalData(button.modal ?? null);
        setActiveModal("directive");
        break;
      case CARD_DIRECTIVE_TYPES.MYCARD_SHOW_DETAILS_SETUPFACECHECK:
        setActiveModal("setupFaceCheck");
        break;
      default:
        break;
    }
  };

  return (
    <>
      <article className={`bg-blue-800 flex-col justify-content-center align-items-center rounded-lg pt-24 overflow-hidden relative ${data.cardImageData.isLocked ? "" : "bg-wave"}`}
      >
        <Alerts directives={data.banners} className="alert-bkgrd-white" />
        <figure className="position-relative mb-24">
          <img src={data.cardImageData.cardImageUrl} alt="credit card" className="max-w-275" />
        </figure>

        <div
          className="w-full text-center py-8 px-16 bg-blue-800 text-white">
          {i18n.t("PAGES.MY_CARD.INDEX.CARD_SECTION.BANNER_TEXT")}{" "}
          {data.cardImageData.isLocked ? (
            <span className="text-text-danger-on-dark bold">{i18n.t("PAGES.MY_CARD.INDEX.CARD_SECTION.LOCKED")}</span>
          ) : (
            <span className="text-text-success-on-dark bold">{i18n.t("PAGES.MY_CARD.INDEX.CARD_SECTION.UNLOCKED")}</span>
          )}
        </div>

        <PINCheckModal
          open={activeModal === "pin"}
          setOpenPinCheckModal={setModalOpen("pin")}
          handleSubmit={submitPin}
          submitting={submitting}
        />

        <CardDetailsModal
          open={activeModal === "cardDetails"}
          setOpenCardDetailsModal={setModalOpen("cardDetails")}
          data={cardDetails}
          isLocked={data.cardImageData.isLocked}
        />

        <MyCardDirectiveModal
          open={activeModal === "directive"}
          setOpen={setModalOpen("directive")}
          modalData={directiveModalData}
        />

        <SetupFaceCheckModal
          open={activeModal === "setupFaceCheck"}
          setOpen={setModalOpen("setupFaceCheck")}
        />
      </article>

      <div className="flex-row mobile-direction-column justify-content-center gap-16 py-16">
        {data.buttons.map((button: APIButtonType, index: number) => (
          <Action
            key={button.id}
            displayType={index === 0 ? ACTION_DISPLAY_TYPES.PRIMARY : ACTION_DISPLAY_TYPES.SECONDARY}
            title={button.text}
            onClick={() => handleClick(button)}
            loading={loadingButtonId === button.id}
            className="w-full"
          />
        ))}
      </div>

    </>
  );
};

export default CardSection;
